import i18n from "i18next";

import "../i18n";
import { proBono } from "../constants/actionTypes";

const defaultModals = {
  upgrade: false,
  payment: false,
  paymentMethod: false,
  addCard: false,
  editCard: false,
  confirm: false,
  confirmTake: false,
  success: false
};

// The store keeps the translated text, so the fallbacks resolve through i18next at the moment a failure is reduced (this reads the
// global i18n instance; `import "../i18n"` initialises it). Not module-level constants: those would freeze the text at import.
const GET_LEADS_ERROR_KEY = "dashboard_probono_error_load_leads";
const TAKE_LEAD_ERROR_KEY = "dashboard_probono_error_take_lead";
const SET_INTEREST_ERROR_KEY = "dashboard_probono_error_set_interest";

// The server answers a failed request with `{ ok: false, error: "<message>" }`. A request that never got an answer (offline, timeout)
// reaches the reducer as a bare string such as "Network Error", which is not for members, so it gets the fallback too.
const getErrorMessage = (body, fallback) => {
  const message = body && typeof body === "object" ? body.error || body.message : null;
  return typeof message === "string" && message ? message : fallback;
};

const initialState = {
  loading: false,
  error: null,
  pendingTake: null,
  leads: [],
  filters: {},
  selectedListingId: null,
  selectedPlanData: null,
  pagination: {
    currentPage: 1,
    currentPageNum: 0,
    lastPage: 1,
    lastPageNum: 0,
    perPage: 10
  },
  activeTab: "new",
  activeSubTab: "all",
  tabsCounter: {
    new: 0,
    accepted: 0,
    hidden: 0
  },
  modals: {
    ...defaultModals
  },
  leadsStats: null
};

export default (state = initialState, action) => {
  switch (action.type) {
    case proBono.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case proBono.GET_LEADS: {
      const parsePage = value => {
        if (value == null || value === "") {
          return 1;
        }
        if (typeof value === "number") {
          return value;
        }
        if (typeof value === "string" && /^\d+$/.test(value)) {
          return parseInt(value, 10);
        }
        return 1;
      };
      const apiLastPage = parsePage(action.payload.lastPage);
      const lastPageNum = Math.max(0, apiLastPage - 1);
      const currentPageNum = Math.max(0, parsePage(action.payload.currentPage) - 1);

      return {
        ...state,
        leads: action.payload.leads,
        filters: action.payload.filters,
        pagination: {
          currentPage: action.payload.currentPage,
          currentPageNum,
          lastPage: action.payload.lastPage,
          lastPageNum,
          perPage: action.payload.perPage
        },
        tabsCounter: action.payload.tabsCounter,
        loading: false
      };
    }

    case proBono.GET_LEADS_FAIL:
      return {
        ...state,
        loading: false,
        error: getErrorMessage(action.payload?.data, i18n.t(GET_LEADS_ERROR_KEY))
      };

    case proBono.SET_LEADS_CLEAR:
      return {
        ...state,
        leads: [],
        filters: {}
      };

    case proBono.SET_TAB:
      return {
        ...state,
        activeTab: action.payload
      };

    case proBono.SET_SUB_TAB:
      return {
        ...state,
        activeSubTab: action.payload
      };

    case proBono.TOGGLE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload.modal]: action.payload.isActive
        }
      };

    case proBono.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultModals
        }
      };

    case proBono.SET_SELECTED_LISTING_ID:
      return {
        ...state,
        selectedListingId: action.payload
      };

    case proBono.SET_SELECTED_PLAN_DATA:
      return {
        ...state,
        selectedPlanData: action.payload
      };

    case proBono.GET_LEADS_STATS_SUCCESS:
      return {
        ...state,
        leadsStats: action.payload
      };

    case proBono.GET_LEADS_STATS_FAIL:
      return {
        ...state
      };

    case proBono.SET_CASE_INTEREST: {
      const target = state.leads.find(item => item.lead_id === action.payload.leadId);
      // A second answer for the same lead (a double click on Hide) finds it already gone: nothing is left to move or count.
      if (!target) {
        return {
          ...state,
          loading: false
        };
      }
      const filteredLeads = state.leads.filter(item => item.lead_id !== action.payload.leadId);
      const leadSection = target.purchased;
      return {
        ...state,
        leads: filteredLeads,
        loading: false,
        tabsCounter: {
          ...state.tabsCounter,
          [leadSection ? "accepted" : "new"]: action.payload.isInterest
            ? Math.max(0, state.tabsCounter[leadSection ? "accepted" : "new"] - 1)
            : Math.max(0, state.tabsCounter[leadSection ? "accepted" : "new"] + 1),
          hidden: action.payload.isInterest ? state.tabsCounter.hidden + 1 : state.tabsCounter.hidden - 1
        }
      };
    }

    case proBono.TAKE_LEAD_SUCCESS: {
      const target = state.leads.find(item => item.lead_id === action.payload.leadId);
      // The lead is no longer on the page (a repeated answer, or the member changed tab while the take was in flight): the
      // counters came from the server's latest list, so moving one again would count the take twice.
      if (!target) {
        return {
          ...state,
          loading: false
        };
      }
      // Read the flag before the lead leaves the list. The server unhides a lead on take, so a Hidden-tab take leaves Hidden.
      const fromHidden = target.hidden === true || target.hidden === "true";
      const filteredLeads = state.leads.filter(item => item.lead_id !== action.payload.leadId);
      return {
        ...state,
        leads: filteredLeads,
        loading: false,
        tabsCounter: {
          ...state.tabsCounter,
          ...(fromHidden ? { hidden: Math.max(0, state.tabsCounter.hidden - 1) } : { new: Math.max(0, state.tabsCounter.new - 1) }),
          accepted: state.tabsCounter.accepted + 1
        }
      };
    }

    case proBono.TAKE_LEAD_FAIL: {
      return {
        ...state,
        loading: false,
        error: getErrorMessage(action.payload?.error, i18n.t(TAKE_LEAD_ERROR_KEY))
      };
    }

    // GROWTH-7941: another member took the last spot while this page was open.
    case proBono.MARK_LEAD_FULL:
      return {
        ...state,
        leads: state.leads.map(item =>
          String(item.lead_id) === String(action.payload.leadId)
            ? { ...item, spots_taken: Math.max(Number(item.spots_taken) || 0, Number(item.spots_total) || 0) }
            : item
        )
      };

    // GROWTH-7941: a released request leaves Taken and goes back to New; inside 24 hours the free take comes back too. The list is
    // reloaded by the page, so only the counters and the stats are moved here.
    case proBono.RELEASE_LEAD_SUCCESS: {
      const wasListed = state.leads.some(item => String(item.lead_id) === String(action.payload.leadId));
      const stats = state.leadsStats;
      return {
        ...state,
        leads: state.leads.filter(item => String(item.lead_id) !== String(action.payload.leadId)),
        tabsCounter: wasListed
          ? {
              ...state.tabsCounter,
              accepted: Math.max(0, state.tabsCounter.accepted - 1),
              new: state.tabsCounter.new + 1
            }
          : state.tabsCounter,
        leadsStats:
          action.payload.refunded && stats && stats.current_month_leads_count != null
            ? { ...stats, current_month_leads_count: Math.max(0, Number(stats.current_month_leads_count) - 1) }
            : stats
      };
    }

    case proBono.SET_CASE_INTEREST_FAIL:
      return {
        ...state,
        loading: false,
        error: getErrorMessage(action.payload?.error, i18n.t(SET_INTEREST_ERROR_KEY))
      };

    case proBono.RESET_ERROR:
      return {
        ...state,
        error: null
      };

    case proBono.SET_PENDING_TAKE:
      return {
        ...state,
        pendingTake: action.payload
      };

    default:
      return state;
  }
};
